import React, { useState, lazy, Suspense } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Typography,
  AppBar,
  Toolbar,
  Button,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  PersonAdd as PersonAddIcon,
  DevicesOther as DevicesOtherIcon,
  SupervisorAccount as SupervisorAccountIcon,
  Menu as MenuIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { useAuth } from 'react-oidc-context';
const MainView = React.lazy(() => import('./MainView'));

const menuItems = [
  { key: 'main', label: 'Dashboard', icon: <DashboardIcon /> },
  { key: 'register-senior', label: 'Register Senior', icon: <PersonAddIcon /> },
  {
    key: 'register-device',
    label: 'Register Device',
    icon: <DevicesOtherIcon />,
  },
  {
    key: 'register-caregiver',
    label: 'Register Caregiver',
    icon: <SupervisorAccountIcon />,
  },
];

const CreateSenior = React.lazy(() => import('./CreateSenior'));

const CreateDevice = React.lazy(() => import('./CreateDevice'));

const CreateCaregiver = React.lazy(() => import('./CreateCaregiver'));

const Dashboard: React.FC = () => {
  const [selected, setSelected] = useState('main');
  const [mobileOpen, setMobileOpen] = useState(false);
  const auth = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Debug: Log the user profile to see available attributes
  console.log('User profile:', auth.user?.profile);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSignOut = () => {
    auth.removeUser();
  };

  const handleMenuItemClick = (key: string) => {
    setSelected(key);
    if (isMobile) {
      setMobileOpen(false); // Close mobile menu after selection
    }
  };

  const drawerWidth = 260;

  const drawerContent = (
    <div>
      <Box
        sx={{
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 600,
          fontSize: 18,
          letterSpacing: '-0.025em',
          color: '#374151',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        }}
      >
        Navigation
      </Box>
      <List sx={{ pt: 2 }}>
        {menuItems.map(item => (
          <ListItem key={item.key} disablePadding sx={{ px: 1.5, mb: 0.5 }}>
            <ListItemButton
              selected={selected === item.key}
              onClick={() => handleMenuItemClick(item.key)}
              sx={{
                borderRadius: 2,
                transition: 'all 0.2s ease',
                '&.Mui-selected': {
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                  '&:hover': {
                    backgroundColor: '#2563eb',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'white',
                  },
                },
                '&:not(.Mui-selected):hover': {
                  backgroundColor: 'rgba(59, 130, 246, 0.08)',
                  transform: 'translateX(4px)',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 40,
                  color: selected === item.key ? 'white' : '#6b7280',
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                sx={{
                  '& .MuiTypography-root': {
                    fontWeight: selected === item.key ? 600 : 500,
                    fontSize: '0.95rem',
                  },
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </div>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        bgcolor: '#f8fafc',
        backgroundImage:
          'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.5) 1px, transparent 0)',
        backgroundSize: '20px 20px',
      }}
    >
      {/* Top App Bar */}
      <AppBar
        position='fixed'
        elevation={0}
        sx={{
          zIndex: theme => theme.zIndex.drawer + 1,
          bgcolor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          color: '#1f2937',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isMobile && (
              <IconButton
                color='inherit'
                aria-label='open drawer'
                edge='start'
                onClick={handleDrawerToggle}
                sx={{ mr: 1 }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <img
              src='/synclogo.png'
              alt='Sync Labs Logo'
              style={{
                height: '32px',
                width: 'auto',
                objectFit: 'contain',
              }}
            />
            {!isMobile && (
              <Typography
                variant='h6'
                component='div'
                sx={{
                  fontWeight: 600,
                  letterSpacing: '-0.025em',
                  color: '#111827',
                }}
              >
                Alice Dashboard
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                display: { xs: 'none', sm: 'flex' },
                flexDirection: 'column',
                alignItems: 'flex-end',
              }}
            >
              <Typography
                variant='body2'
                sx={{
                  color: '#6b7280',
                  fontWeight: 500,
                }}
              >
                {auth.user?.profile?.email}
              </Typography>
              <Typography
                variant='caption'
                sx={{
                  color: '#9ca3af',
                  fontSize: '0.75rem',
                  fontWeight: 400,
                }}
              >
                {(() => {
                  const profile = auth.user?.profile;
                  const cognitoGroups = profile?.['cognito:groups'] as
                    | string[]
                    | undefined;
                  const groups = profile?.groups as string[] | undefined;
                  const group = profile?.group as string | undefined;

                  return (
                    cognitoGroups?.[0] ||
                    groups?.[0] ||
                    group ||
                    'No group assigned'
                  );
                })()}
              </Typography>
            </Box>
            {isMobile ? (
              <IconButton
                color='inherit'
                onClick={handleSignOut}
                sx={{
                  color: '#ef4444',
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                  borderRadius: 2,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: '#ef4444',
                    color: 'white',
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                <LogoutIcon sx={{ fontSize: '1.2rem' }} />
              </IconButton>
            ) : (
              <Button
                variant='outlined'
                size='small'
                onClick={handleSignOut}
                sx={{
                  color: '#ef4444',
                  borderColor: 'rgba(239, 68, 68, 0.3)',
                  borderRadius: 2,
                  fontWeight: 500,
                  textTransform: 'none',
                  px: 2,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: '#ef4444',
                    color: 'white',
                    borderColor: '#ef4444',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
                  },
                }}
              >
                Sign Out
              </Button>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Navigation Drawer */}
      <Box
        component='nav'
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant='temporary'
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              marginTop: '64px',
              height: 'calc(100vh - 64px)',
              bgcolor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
            },
          }}
        >
          {drawerContent}
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant='permanent'
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              borderRight: '1px solid rgba(0, 0, 0, 0.08)',
              marginTop: '64px',
              height: 'calc(100vh - 64px)',
            },
          }}
          open
        >
          {drawerContent}
        </Drawer>
      </Box>
      {/* Main Content Area */}
      <Box
        component='main'
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3, md: 4 },
          width: { md: `calc(100% - ${drawerWidth}px)` },
          marginTop: '64px', // Account for AppBar height
        }}
      >
        <Typography
          variant='h4'
          gutterBottom
          sx={{
            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
            fontWeight: 700,
            letterSpacing: '-0.025em',
            color: '#111827',
            mb: 3,
          }}
        >
          {menuItems.find(item => item.key === selected)?.label}
        </Typography>
        {selected === 'main' && (
          <Suspense fallback={<div>Loading...</div>}>
            <MainView />
          </Suspense>
        )}
        {selected === 'register-senior' && (
          <Suspense fallback={<div>Loading...</div>}>
            <CreateSenior />
          </Suspense>
        )}
        {selected === 'register-device' && (
          <Suspense fallback={<div>Loading...</div>}>
            <CreateDevice />
          </Suspense>
        )}
        {selected === 'register-caregiver' && (
          <Suspense fallback={<div>Loading...</div>}>
            <CreateCaregiver />
          </Suspense>
        )}
        {/* Add similar logic for other tabs if needed */}
      </Box>
    </Box>
  );
};

export default Dashboard;
